import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ListTree, Plus, Trash2 } from 'lucide-react';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { PublisherXpParameter } from '../../model';
import type { PublisherFormValue } from '../../publisherFormSchema';

const xpOptions = Object.values(PublisherXpParameter);

export function PublisherXpMappingsFields() {
  'use no memo';
  const { control, register, setValue, formState } = useFormContext<PublisherFormValue>();
  const { fields, append, remove } = useFieldArray({ control, name: 'xpParametersMappings' });
  const mappings = useWatch({ control, name: 'xpParametersMappings' });
  const selected = mappings.map((mapping) => mapping.xpParameter);
  const selectedSet = new Set<string | undefined>(selected);
  return (
    <DashboardPageFormBody width="lg">
      <section className="border-input overflow-hidden rounded-md border">
        <header className="bg-muted/40 border-input flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">{dashboardT('publishers.xpTitle')}</h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {dashboardT('publishers.xpDescription')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= 10}
            onClick={() => {
              append({ value: '', xpParameter: '' });
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {dashboardT('publishers.addXp')}
          </Button>
        </header>
        {fields.length ? (
          <div className="divide-border divide-y">
            {fields.map((field, index) => {
              const mappingErrors = formState.errors.xpParametersMappings?.[index];
              const valueError = mappingErrors?.value?.message;
              const parameterError = mappingErrors?.xpParameter?.message;
              const valueErrorId = `xp-value-error-${field.id}`;
              const parameterErrorId = `xp-parameter-error-${field.id}`;
              return (
                <div
                  key={field.id}
                  className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_220px_36px] sm:items-start"
                >
                  <div className="grid grid-rows-[16px_36px_auto] gap-1.5">
                    <Label
                      className="h-4 text-[13px] leading-4 font-semibold"
                      htmlFor={`xp-value-${field.id}`}
                    >
                      {dashboardT('publishers.xpValue')}
                    </Label>
                    <Input
                      id={`xp-value-${field.id}`}
                      className="h-9"
                      placeholder={dashboardT('publishers.xpValuePlaceholder')}
                      aria-describedby={valueError ? valueErrorId : undefined}
                      aria-invalid={Boolean(valueError)}
                      {...register(mappingFieldPath(index, 'value'))}
                    />
                    {valueError ? (
                      <p id={valueErrorId} role="alert" className="text-destructive text-xs">
                        {valueError}
                      </p>
                    ) : null}
                  </div>
                  <div className="grid grid-rows-[16px_36px_auto] gap-1.5">
                    <Label
                      className="h-4 text-[13px] leading-4 font-semibold"
                      htmlFor={`xp-parameter-${field.id}`}
                    >
                      {dashboardT('publishers.xpParameter')}
                    </Label>
                    <Select
                      value={mappings[index]?.xpParameter ?? ''}
                      onValueChange={(value) => {
                        setValue(
                          mappingFieldPath(index, 'xpParameter'),
                          value as PublisherXpParameter,
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }}
                    >
                      <SelectTrigger
                        id={`xp-parameter-${field.id}`}
                        className="h-9"
                        aria-label={dashboardT('publishers.xpParameter')}
                        aria-describedby={parameterError ? parameterErrorId : undefined}
                        aria-invalid={Boolean(parameterError)}
                      >
                        <SelectValue placeholder={dashboardT('publishers.select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {xpOptions.map((option) => (
                          <SelectItem
                            key={option}
                            value={option}
                            disabled={selectedSet.has(option) && selected[index] !== option}
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {parameterError ? (
                      <p id={parameterErrorId} role="alert" className="text-destructive text-xs">
                        {parameterError}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 sm:mt-[22px]"
                    aria-label={dashboardT('publishers.removeXp')}
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-10 text-center">
            <ListTree className="text-muted-foreground mx-auto h-6 w-6" aria-hidden="true" />
            <p className="text-muted-foreground mt-2 text-sm">{dashboardT('publishers.xpEmpty')}</p>
          </div>
        )}
      </section>
      {formState.errors.xpParametersMappings?.message ? (
        <p role="alert" className="text-destructive text-xs">
          {formState.errors.xpParametersMappings.message}
        </p>
      ) : null}
    </DashboardPageFormBody>
  );
}

function mappingFieldPath<TField extends 'value' | 'xpParameter'>(
  index: number,
  field: TField,
): `xpParametersMappings.${number}.${TField}` {
  // RHF models field-array indexes as numeric template-literal paths.
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `xpParametersMappings.${index}.${field}`;
}
